import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';

import { ProgramOverview } from '@/components/ProgramOverview';
import { setDatadogGlobalContext, trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, LoadingScreen, Screen, ScreenHeading } from '@/modules/ui';

/**
 * FULL CHOICE PROGRAM REVIEW — the detail/confirm screen after the user picks a
 * program from the list (Sparta's FullChoiceProgramReviewScreen). "Join"
 * enrolls (the driver then advances to coach matching); "See other options"
 * clears the selection and returns to the list. On refresh the driver re-routes
 * to the list (no durable selection), so a missing program here is transient.
 */
export function FullChoiceProgramReviewScreen() {
  const t = useT();
  const {
    prescribedPrograms,
    selectedProgramUuid,
    setSelectedProgram,
    setCurrentRoute,
    enrollInProgram,
    coachingModality,
    user,
  } = useRulesEngine();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const program = prescribedPrograms?.programs.find((p) => p.uuid === selectedProgramUuid);

  const onJoin = async () => {
    if (program) {
      trackEvent('program-overview-joined-program', {
        uuid: user?.uuid,
        programUrn: program.urn,
        programTitle: program.title,
      });
      void setDatadogGlobalContext({ selectedProgram: program.title });
    }
    setSubmitError(false);
    try {
      setIsSubmitting(true);
      // On success the driver routes away (programInstances → coach matching),
      // so this screen unmounts — only reset the spinner on failure.
      await enrollInProgram();
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  const onSeeOtherOptions = () => {
    trackEvent('program-overview-expand-selection', {
      uuid: user?.uuid,
      fromProgramUrn: program?.urn,
    });
    setSelectedProgram(undefined);
    setCurrentRoute('/step/program-matching');
  };

  if (!program) return <LoadingScreen />;

  return (
    <Screen
      busy={isSubmitting}
      footer={
        <VStack space='md'>
          <CtaButton
            isBusy={isSubmitting}
            isDisabled={isSubmitting}
            onPress={onJoin}
            testID='full-choice-join'
          >
            <T _str='Join program' />
          </CtaButton>
          <CtaButton
            type='secondary'
            onPress={onSeeOtherOptions}
            testID='full-choice-see-other-options'
          >
            <T _str='See other options' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading title={t('Review your program details below')} />
        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong joining the program. Please try again.' />
          </Text>
        ) : null}
        <ProgramOverview program={program} modality={coachingModality} />
      </VStack>
    </Screen>
  );
}
